'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { MetaDefault } from '../../../../../components/typography';
import { Input } from '../../../../../components/styled/input';
import { FlatSignalSummary } from './flat-signal-summary';
import { IconButton } from '../../../../../components/icon-button';
import { Card } from '../../../../../components/card';
import { Spinner } from '../../../../../components/spinner';
import { Combobox } from '../../../../../components/combobox';
import {
  Alert01Icon,
  Tick01Icon,
  ArrowUpDownIcon,
  Target03Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useFindingSearchContext } from '../../../finding-search/ChatFindingSearchProvider';
import type { SearchSignalResult } from '../../../finding-search/ChatFindingSearchProvider';
import { SignalCard } from '../signal/SignalCard';
import type { Signal } from '../signal/types';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import { createListCollection } from '@ark-ui/react';

type SignalSelectorProps = {
  selectedSignalId?: string;
  onSignalSelect?: (signalId: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  flat?: boolean;
};

function truncateToLines(
  text: string | undefined,
  lines: number,
): string | undefined {
  if (!text) return text;
  const maxChars = lines * 100;

  if (text.length <= maxChars) return text;

  return text.slice(0, maxChars).trim() + '...';
}

function toSignal(signal: SearchSignalResult, truncate = false): Signal {
  return {
    id: signal.id,
    title: truncate ? truncateToLines(signal.label, 3) : signal.label,
    category: signal.category,
    description: truncate
      ? truncateToLines(signal.description, 3)
      : signal.description,
    endpointId: signal.endpointId,
    harEntryId: signal.harEntryId,
    entryPointId: signal.entryPointId,
    url: signal.url,
    method: signal.method,
    createdAt: signal.createdAt,
  };
}

export function SignalSelector({
  selectedSignalId,
  onSignalSelect,
  disabled = false,
  readOnly = false,
  flat = false,
}: SignalSelectorProps) {
  const t = useTranslations('designSystem.chat.signalSelector');
  const { onSearchSignals, onGetSignalById } = useFindingSearchContext();
  const [selectedSignal, setSelectedSignal] =
    useState<SearchSignalResult | null>(null);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchSignalResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchSignal = async () => {
      if (!selectedSignalId) {
        setSelectedSignal(null);
        setLoadError(false);

        return;
      }

      if (!onGetSignalById) {
        setSelectedSignal(null);
        setLoadError(true);

        return;
      }

      setIsLoadingSelected(true);
      setLoadError(false);

      try {
        const signal = await onGetSignalById(selectedSignalId);

        setSelectedSignal(signal ?? null);
        setLoadError(!signal);
      } catch (error) {
        console.error('Failed to fetch signal:', error);
        setSelectedSignal(null);
        setLoadError(true);
      } finally {
        setIsLoadingSelected(false);
      }
    };

    fetchSignal();
  }, [selectedSignalId, onGetSignalById]);
  const signalsCollection = useMemo(() => {
    return createListCollection({
      items: searchResults.map((signal) => ({
        label: signal.label,
        value: signal.id,
        category: signal.category,
        description: signal.description,
      })),
    });
  }, [searchResults]);
  const handleInputValueChange = useCallback(
    (details: { inputValue: string }) => {
      const query = details.inputValue;

      setInputValue(query);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      if (!onSearchSignals || !query.trim()) {
        setSearchResults([]);
        setIsSearching(false);

        return;
      }

      setIsSearching(true);
      searchTimeout.current = setTimeout(async () => {
        try {
          const results = await onSearchSignals(query);

          setSearchResults(results);
        } catch (error) {
          console.error('Failed to search signals:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [onSearchSignals],
  );
  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const selectedId = details.value[0];

      if (!selectedId) return;
      const signal = searchResults.find((s) => s.id === selectedId);

      if (signal) {
        setSelectedSignal(signal);
        onSignalSelect?.(signal.id);
        setInputValue('');
        setSearchResults([]);
      }
    },
    [searchResults, onSignalSelect],
  );

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);
  if (readOnly && flat) {
    if (isLoadingSelected) {
      return <MetaDefault color="fg.muted">{t('loading')}</MetaDefault>;
    }

    if (!selectedSignal && selectedSignalId) {
      return (
        <MetaDefault color={loadError ? 'fg.error' : 'fg.muted'}>
          {loadError ? t('signalNotFound') : t('loadingSignal')}
        </MetaDefault>
      );
    }

    if (!selectedSignal) {
      return (
        <MetaDefault color="fg.error">{t('noSignalSelected')}</MetaDefault>
      );
    }

    return (
      <FlatSignalSummary
        label={selectedSignal.label}
        description={selectedSignal.description}
        category={selectedSignal.category}
      />
    );
  }

  if (readOnly) {
    if (isLoadingSelected) {
      return (
        <HStack gap={2} alignItems="center" p={2} width="100%">
          <Spinner size="sm" />
          <Text fontSize="xs" color="fg.muted">
            {t('loading')}
          </Text>
        </HStack>
      );
    }

    if (!selectedSignal && selectedSignalId) {
      if (loadError) {
        return (
          <Card.Root flexShrink={0} width="100%">
            <Card.Body p={2.5}>
              <HStack gap={2} alignItems="center" width="100%">
                <Alert01Icon size={14} color="var(--colors-fg-error)" />
                <Text fontSize="xs" color="fg.error">
                  {t('signalNotFound')}
                </Text>
              </HStack>
            </Card.Body>
          </Card.Root>
        );
      }

      return (
        <Card.Root flexShrink={0} width="100%">
          <Card.Body p={2.5}>
            <HStack gap={2} alignItems="center" width="100%">
              <Target03Icon size={14} color="var(--colors-orange-9)" />
              <Spinner size="xs" />
              <Text fontSize="xs" color="fg.muted">
                {t('loadingSignal')}
              </Text>
            </HStack>
          </Card.Body>
        </Card.Root>
      );
    }

    if (!selectedSignal) {
      return (
        <HStack gap={2} alignItems="center" width="100%">
          <Alert01Icon size={14} color="var(--colors-fg-error)" />
          <Text fontSize="xs" color="fg.error">
            {t('noSignalSelected')}
          </Text>
        </HStack>
      );
    }

    return (
      <Box width="100%">
        <SignalCard signal={toSignal(selectedSignal, true)} variant="compact" />
      </Box>
    );
  }

  return (
    <VStack alignItems="start" gap={3} width="100%">
      {isLoadingSelected ? (
        <HStack gap={2} alignItems="center" p={2} width="100%">
          <Spinner size="sm" />
          <Text fontSize="xs" color="fg.muted">
            {t('loading')}
          </Text>
        </HStack>
      ) : selectedSignal ? (
        <Box width="100%">
          <SignalCard
            signal={toSignal(selectedSignal, true)}
            variant="compact"
          />
        </Box>
      ) : selectedSignalId ? (
        <Card.Root flexShrink={0} width="100%">
          <Card.Body p={2.5}>
            <HStack gap={2} alignItems="center" width="100%">
              {loadError ? (
                <>
                  <Alert01Icon size={14} color="var(--colors-fg-error)" />
                  <Text fontSize="xs" color="fg.error">
                    {t('signalNotFound')}
                  </Text>
                </>
              ) : (
                <>
                  <Target03Icon size={14} color="var(--colors-orange-9)" />
                  <Spinner size="xs" />
                  <Text fontSize="xs" color="fg.muted">
                    {t('loadingSignal')}
                  </Text>
                </>
              )}
            </HStack>
          </Card.Body>
        </Card.Root>
      ) : null}

      {!disabled && onSearchSignals && (
        <Combobox.Root
          collection={signalsCollection}
          value={selectedSignalId ? [selectedSignalId] : []}
          inputValue={inputValue}
          onValueChange={handleValueChange}
          onInputValueChange={handleInputValueChange}
          size="sm"
          openOnClick
          allowCustomValue
        >
          <Combobox.Control>
            <Combobox.Input placeholder={t('searchPlaceholder')} asChild>
              <Input size="sm" />
            </Combobox.Input>
            <Combobox.Trigger asChild>
              <IconButton variant="ghost" aria-label="Open signals" size="xs">
                <ArrowUpDownIcon size={14} />
              </IconButton>
            </Combobox.Trigger>
          </Combobox.Control>

          <Combobox.Positioner>
            <Combobox.Content maxH="300px" overflowY="auto">
              <Combobox.List>
                {isSearching ? (
                  <HStack
                    gap={2}
                    alignItems="center"
                    p={3}
                    justifyContent="center"
                  >
                    <Spinner size="sm" />
                    <Text fontSize="xs" color="fg.muted">
                      {t('searching')}
                    </Text>
                  </HStack>
                ) : signalsCollection.items.length > 0 ? (
                  signalsCollection.items.map((item, index) => (
                    <Combobox.Item
                      key={item.value}
                      item={item}
                      borderBottomWidth={
                        index < signalsCollection.items.length - 1 ? '1px' : '0'
                      }
                      borderBottomColor="border.subtle"
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-start"
                      py={2}
                      px={2}
                      minH="auto"
                      h="auto"
                    >
                      <VStack
                        gap={0.5}
                        alignItems="flex-start"
                        flex={1}
                        minW={0}
                      >
                        <HStack gap={2} width="100%" alignItems="center">
                          <Target03Icon
                            size={12}
                            color="var(--colors-orange-9)"
                            style={{ flexShrink: 0 }}
                          />
                          <Combobox.ItemText asChild>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {item.label}
                            </Text>
                          </Combobox.ItemText>
                        </HStack>
                        {item.category && (
                          <Text fontSize="2xs" color="fg.muted" pl={5}>
                            {formatCategoryForDisplay(item.category)}
                          </Text>
                        )}
                        {item.description && (
                          <Text
                            fontSize="xs"
                            color="fg.muted"
                            lineClamp={1}
                            pl={5}
                          >
                            {item.description}
                          </Text>
                        )}
                      </VStack>
                      <Combobox.ItemIndicator>
                        <Tick01Icon size={14} />
                      </Combobox.ItemIndicator>
                    </Combobox.Item>
                  ))
                ) : (
                  <Text fontSize="xs" color="fg.muted" p={3} textAlign="center">
                    {t('typeToSearch')}
                  </Text>
                )}
              </Combobox.List>
            </Combobox.Content>
          </Combobox.Positioner>
        </Combobox.Root>
      )}

      {!disabled && !onSearchSignals && (
        <Text fontSize="xs" color="fg.muted">
          {t('searchNotAvailable')}
        </Text>
      )}
    </VStack>
  );
}
